import { normalizeRestoreView } from "./map-bridge-input.js";
import { normalizeSelectionSnapshotValue } from "./map-bridge-output.js";

const STORAGE_KEY = "fishystuff.map.session.v2";
const DOCUMENT_VERSION = 2;
const DOCUMENT_FIELDS = Object.freeze([
  "documentVersion",
  "revision",
  "selectionTarget",
  "view",
]);
const MAX_STORED_LENGTH = 16_384;
const MAX_REVISION_LENGTH = 128;
const BROWSER_REPOSITORY_PROPERTY = "__fishystuffMapSessionRepository";
const REPOSITORY_BRAND = Symbol.for("fishystuff.map-session-repository.v2");
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-map-session-repository.v2",
);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function invalid(reason) {
  return { kind: "invalid", reason };
}

function unavailable(operation, reason) {
  return { kind: "unavailable", operation, reason };
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return Object.freeze(value);
}

function frozenClone(value) {
  return deepFreeze(cloneJson(value));
}

function jsonValuesEqual(left, right) {
  if (typeof left === "number" || typeof right === "number") {
    return typeof left === "number"
      && typeof right === "number"
      && Object.is(left, right);
  }
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left)
      && Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => jsonValuesEqual(value, right[index]));
  }
  if (!isRecord(left) || !isRecord(right)) {
    return false;
  }
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key, index) => (
      key === rightKeys[index]
      && jsonValuesEqual(left[key], right[key])
    ));
}

function canonicalView(value) {
  if (!isRecord(value)) {
    throw new TypeError("Map session view must be an object.");
  }
  const view = normalizeRestoreView(value);
  if (!view) {
    throw new TypeError("Map session view is not restorable.");
  }
  return view;
}

function canonicalSelectionTarget(value) {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    throw new TypeError("Map session selection target must be an object or null.");
  }
  const target = normalizeSelectionSnapshotValue({
    detailsTarget: value,
  }).detailsTarget;
  if (!target) {
    throw new TypeError("Map session selection target is not restorable.");
  }
  return target;
}

function decodeStoredDocument(raw) {
  if (typeof raw !== "string") {
    return invalid("stored-value-must-be-a-string");
  }
  if (raw.length > MAX_STORED_LENGTH) {
    return invalid("stored-value-exceeds-v2-limit");
  }

  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return invalid("malformed-json");
  }

  if (!isRecord(document)) {
    return invalid("document-must-be-an-object");
  }
  if (
    Number.isSafeInteger(document.documentVersion)
    && document.documentVersion > DOCUMENT_VERSION
  ) {
    return {
      kind: "unsupportedVersion",
      foundVersion: document.documentVersion,
    };
  }

  const fields = Object.keys(document).sort();
  if (
    fields.length !== DOCUMENT_FIELDS.length
    || fields.some((field, index) => field !== DOCUMENT_FIELDS[index])
  ) {
    return invalid("document-fields-must-match-v2");
  }
  if (document.documentVersion !== DOCUMENT_VERSION) {
    return invalid("document-version-must-be-2");
  }
  if (
    typeof document.revision !== "string"
    || document.revision.length === 0
    || document.revision.length > MAX_REVISION_LENGTH
  ) {
    return invalid("revision-must-be-a-bounded-non-empty-string");
  }

  let view;
  try {
    view = canonicalView(document.view);
  } catch (_error) {
    return invalid("view-must-be-restorable");
  }
  if (!jsonValuesEqual(document.view, view)) {
    return invalid("view-must-be-canonical");
  }

  let selectionTarget;
  try {
    selectionTarget = canonicalSelectionTarget(document.selectionTarget);
  } catch (_error) {
    return invalid("selection-target-must-be-restorable");
  }
  if (!jsonValuesEqual(document.selectionTarget, selectionTarget)) {
    return invalid("selection-target-must-be-canonical");
  }

  return {
    kind: "ready",
    revision: document.revision,
    view,
    selectionTarget,
  };
}

function defaultCreateRevision() {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function selectionCommandOptions(value) {
  if (!isRecord(value)) {
    throw new TypeError("Selection target options must be an object.");
  }
  const fields = Object.keys(value);
  if (fields.length !== 1 || fields[0] !== "currentView") {
    throw new TypeError(
      "Selection target options must contain only currentView.",
    );
  }
  return { currentView: canonicalView(value.currentView) };
}

function readyStateFromDocument(document) {
  return {
    kind: "ready",
    revision: document.revision,
    view: document.view,
    selectionTarget: document.selectionTarget,
  };
}

function documentFromReadyState(state) {
  return {
    documentVersion: DOCUMENT_VERSION,
    revision: state.revision,
    view: cloneJson(state.view),
    selectionTarget: cloneJson(state.selectionTarget),
  };
}

function assertDocumentSize(view, selectionTarget) {
  const maximumRevisionDocument = {
    documentVersion: DOCUMENT_VERSION,
    revision: "r".repeat(MAX_REVISION_LENGTH),
    view,
    selectionTarget,
  };
  if (JSON.stringify(maximumRevisionDocument).length > MAX_STORED_LENGTH) {
    throw new RangeError("Map session document exceeds the v2 size limit.");
  }
}

/**
 * Owns the restorable state of one map tab.
 *
 * Applied bridge output updates the in-memory document. SessionStorage is a
 * lifecycle checkpoint only, so hot view and selection events never perform a
 * provider write.
 */
export function createMapSessionRepository({
  storage = null,
  pageEventTarget = null,
  visibilityTarget = null,
  createRevision = defaultCreateRevision,
} = {}) {
  if (typeof createRevision !== "function") {
    throw new TypeError("createRevision must be a function.");
  }

  let currentState = frozenClone({ kind: "uninitialized" });
  let workingDocument = null;
  let dirty = false;
  let resetPending = false;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return frozenClone(currentState);
  }

  function deliver(listener, change) {
    try {
      listener(state(), change);
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState, cause) {
    const frozen = frozenClone(nextState);
    if (jsonValuesEqual(currentState, frozen)) {
      return state();
    }
    currentState = frozen;
    const change = Object.freeze({ cause });
    if (!disposed) {
      for (const listener of listeners) {
        deliver(listener, change);
      }
    }
    return state();
  }

  function readAuthoritativeState() {
    let raw;
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Session storage is unavailable.");
      }
      raw = storage.getItem(STORAGE_KEY);
    } catch (_error) {
      return unavailable("read", "storage-read-failed");
    }
    if (raw === null) {
      return { kind: "missing" };
    }
    return decodeStoredDocument(raw);
  }

  function loadFromStorage(cause) {
    if (resetPending) {
      return state();
    }
    const authoritative = readAuthoritativeState();
    if (dirty && workingDocument) {
      if (
        authoritative.kind === "ready"
        && jsonValuesEqual(
          documentFromReadyState(authoritative),
          workingDocument,
        )
      ) {
        dirty = false;
        return publish(authoritative, cause);
      }
      return publish({
        kind: "conflict",
        reason: "dirty-working-state-diverged-from-authority",
        pending: readyStateFromDocument(workingDocument),
        authoritative,
      }, cause);
    }
    if (authoritative.kind === "ready") {
      workingDocument = documentFromReadyState(authoritative);
    } else {
      workingDocument = null;
    }
    dirty = false;
    return publish(authoritative, cause);
  }

  function load() {
    return loadFromStorage("load");
  }

  function ensureInitialized() {
    if (currentState.kind === "uninitialized") {
      loadFromStorage("load");
    }
  }

  function nextRevision() {
    try {
      const revision = createRevision();
      if (
        typeof revision !== "string"
        || revision.length === 0
        || revision.length > MAX_REVISION_LENGTH
        || revision === workingDocument?.revision
      ) {
        throw new TypeError("Revision must be a new bounded non-empty string.");
      }
      return revision;
    } catch (_error) {
      return null;
    }
  }

  function mutationIsBlocked() {
    return resetPending
      || currentState.kind === "invalid"
      || currentState.kind === "unsupportedVersion"
      || currentState.kind === "conflict"
      || (currentState.kind === "unavailable" && !workingDocument);
  }

  function recordDocument(nextValues, cause) {
    assertDocumentSize(nextValues.view, nextValues.selectionTarget);
    ensureInitialized();
    if (mutationIsBlocked()) {
      return state();
    }

    const currentView = workingDocument?.view ?? null;
    const currentSelectionTarget = workingDocument?.selectionTarget ?? null;
    if (
      currentView !== null
      && jsonValuesEqual(currentView, nextValues.view)
      && jsonValuesEqual(currentSelectionTarget, nextValues.selectionTarget)
    ) {
      return state();
    }

    const revision = nextRevision();
    if (!revision) {
      return publish(
        unavailable("record", "revision-generation-failed"),
        cause,
      );
    }
    workingDocument = {
      documentVersion: DOCUMENT_VERSION,
      revision,
      view: cloneJson(nextValues.view),
      selectionTarget: cloneJson(nextValues.selectionTarget),
    };
    dirty = true;
    if (currentState.kind === "unavailable") {
      return state();
    }
    return publish(readyStateFromDocument(workingDocument), cause);
  }

  async function recordView(value) {
    const view = canonicalView(value);
    ensureInitialized();
    return recordDocument({
      view,
      selectionTarget: workingDocument?.selectionTarget ?? null,
    }, "recordView");
  }

  async function recordSelectionTarget(value, options) {
    const selectionTarget = canonicalSelectionTarget(value);
    const { currentView } = selectionCommandOptions(options);
    return recordDocument({
      view: currentView,
      selectionTarget,
    }, "recordSelectionTarget");
  }

  async function checkpoint() {
    ensureInitialized();
    if (
      resetPending
      || currentState.kind === "conflict"
      || !dirty
      || !workingDocument
      || mutationIsBlocked()
    ) {
      return state();
    }
    try {
      if (!storage || typeof storage.setItem !== "function") {
        throw new TypeError("Session storage is unavailable.");
      }
      storage.setItem(STORAGE_KEY, JSON.stringify(workingDocument));
    } catch (_error) {
      return publish(
        unavailable("checkpoint", "storage-write-failed"),
        "checkpoint",
      );
    }
    dirty = false;
    return publish(readyStateFromDocument(workingDocument), "checkpoint");
  }

  async function reset() {
    ensureInitialized();
    if (currentState.kind === "missing" && !workingDocument && !resetPending) {
      return state();
    }
    try {
      if (!storage || typeof storage.removeItem !== "function") {
        throw new TypeError("Session storage is unavailable.");
      }
      storage.removeItem(STORAGE_KEY);
    } catch (_error) {
      resetPending = true;
      return publish(
        unavailable("remove", "storage-remove-failed"),
        "reset",
      );
    }
    resetPending = false;
    workingDocument = null;
    dirty = false;
    return publish({ kind: "missing" }, "reset");
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Repository listener must be a function.");
    }
    if (disposed) {
      return () => {};
    }
    listeners.add(listener);
    deliver(listener, Object.freeze({ cause: "current" }));
    return () => {
      listeners.delete(listener);
    };
  }

  function handlePageHide() {
    if (!disposed) {
      void checkpoint();
    }
  }

  function handleVisibilityChange() {
    if (!disposed && visibilityTarget?.visibilityState === "hidden") {
      void checkpoint();
    }
  }

  function handlePageShow(event) {
    if (!disposed && !resetPending && event?.persisted === true) {
      loadFromStorage("pageshow");
    }
  }

  if (pageEventTarget && typeof pageEventTarget.addEventListener === "function") {
    pageEventTarget.addEventListener("pagehide", handlePageHide);
    pageEventTarget.addEventListener("pageshow", handlePageShow);
  }
  if (visibilityTarget && typeof visibilityTarget.addEventListener === "function") {
    visibilityTarget.addEventListener("visibilitychange", handleVisibilityChange);
  }

  function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    listeners.clear();
    if (pageEventTarget && typeof pageEventTarget.removeEventListener === "function") {
      pageEventTarget.removeEventListener("pagehide", handlePageHide);
      pageEventTarget.removeEventListener("pageshow", handlePageShow);
    }
    if (visibilityTarget && typeof visibilityTarget.removeEventListener === "function") {
      visibilityTarget.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: DOCUMENT_VERSION,
    load,
    state,
    recordView,
    recordSelectionTarget,
    checkpoint,
    reset,
    subscribe,
    dispose,
  });
}

let browserRepository = null;

function isCompatibleBrowserRepository(value) {
  return Boolean(
    value
    && value[BROWSER_REPOSITORY_BRAND] === DOCUMENT_VERSION
    && typeof value.state === "function"
    && typeof value.recordView === "function"
    && typeof value.recordSelectionTarget === "function"
    && typeof value.reset === "function"
    && typeof value.subscribe === "function",
  );
}

function browserRepositoryView(repository) {
  return Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: DOCUMENT_VERSION,
    state: repository.state,
    recordView: repository.recordView,
    recordSelectionTarget: repository.recordSelectionTarget,
    reset: repository.reset,
    subscribe: repository.subscribe,
  });
}

export function browserMapSessionRepository() {
  const windowRef = globalThis.window;
  if (windowRef && typeof windowRef === "object") {
    const sharedRepository = windowRef[BROWSER_REPOSITORY_PROPERTY];
    if (sharedRepository !== undefined) {
      if (!isCompatibleBrowserRepository(sharedRepository)) {
        throw new TypeError(
          "The shared map session repository has an incompatible contract.",
        );
      }
      browserRepository = sharedRepository;
    }
  }

  if (!browserRepository) {
    let storage = null;
    try {
      storage = globalThis.sessionStorage;
    } catch (_error) {
      storage = null;
    }
    const repository = createMapSessionRepository({
      storage,
      pageEventTarget: windowRef ?? null,
      visibilityTarget: globalThis.document ?? null,
    });
    repository.load();
    browserRepository = browserRepositoryView(repository);
  }

  if (windowRef && typeof windowRef === "object") {
    windowRef[BROWSER_REPOSITORY_PROPERTY] = browserRepository;
  }
  return browserRepository;
}
